import { useAccount, useDisconnect, useWalletClient, usePublicClient, useSwitchChain } from 'wagmi';
import { useAppKit, useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import { useEffect, useState } from 'react';
import { environment } from '@/config/environment';
import { modal } from '@/config/web3';

export interface LockInfo {
  totalAmount: bigint;
  unlockedAmount: bigint;
  beneficiary: string;
  startTime: bigint;
  vestedAmount: bigint;
  availableAmount: bigint;
  isInitialized: boolean;
}

export interface VestingSchedule {
  date: Date;
  percentage: number;
  amount: bigint;
  unlocked: boolean;
}

export function useWeb3() {
  const { address, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  const { open } = useAppKit();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { switchChain } = useSwitchChain();

  const [isCorrectNetwork, setIsCorrectNetwork] = useState(true);
  const [showNetworkDialog, setShowNetworkDialog] = useState(false);

  // Check if we're on the correct network
  useEffect(() => {
    if (isConnected && chainId) {
      const correct = chainId === environment.chainId;
      setIsCorrectNetwork(correct);
      if (!correct) {
        setShowNetworkDialog(true);
      }
    }
  }, [isConnected, chainId]);

  const connectWallet = async () => {
    try {
      await open();
    } catch (error) {
      console.error('Failed to open wallet modal:', error);
      throw error;
    }
  };

  const disconnectWallet = async () => {
    try {
      await disconnect();
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const openModal = () => {
    open();
  };

  const switchToCorrectNetwork = async () => {
    try {
      await switchChain({ chainId: environment.chainId });
      setShowNetworkDialog(false);
    } catch (error) {
      console.error('Failed to switch network:', error);
      throw error;
    }
  };

  const closeNetworkDialog = () => {
    setShowNetworkDialog(false);
  };

  const formatAddress = (address: string): string => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTokenAmount = (amount: bigint): string => {
    const decimals = environment.tokenDecimals;
    const divisor = BigInt(10 ** decimals);
    const wholePart = amount / divisor;
    const fractionalPart = amount % divisor;
    const fractionalStr = fractionalPart.toString().padStart(decimals, '0');

    // Always show 3 decimal places
    const displayDecimals = 3;
    const truncatedFractional = fractionalStr.slice(0, displayDecimals).padEnd(displayDecimals, '0');

    return `${wholePart}.${truncatedFractional}`;
  };

  return {
    // State
    address,
    isConnected,
    chainId,
    isCorrectNetwork,
    showNetworkDialog,
    walletClient,
    publicClient,

    // Actions
    connectWallet,
    disconnectWallet,
    openModal,
    switchToCorrectNetwork,
    closeNetworkDialog,

    // Utilities
    formatAddress,
    formatTokenAmount,
  };
}
